class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.content = this.mainDetailsToggle.querySelector('.mega-menu__content');
    this.isHoverOnly = this.mainDetailsToggle.hasAttribute('data-hover-only');
    this.openAnimationDuration = 0.4; // Duration for open animation (seconds)
    this.closeAnimationDuration = 0.4; // Match open duration for close animation
    this.animation = null; // Store GSAP animation instance

    // Ensure initial state
    if (this.content) {
      gsap.set(this.content, {
        opacity: 0,
        maxHeight: 0,
        visibility: 'hidden',
        x: 0, // Ensure no initial x transform
        y: 0 // Ensure no initial y transform
      });
    }

    // Hover events
    this.mainDetailsToggle.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.mainDetailsToggle.addEventListener('mouseleave', this.onMouseLeave.bind(this));

    // Disable click toggling for hover-only menus
    if (this.isHoverOnly) {
      this.summary.addEventListener('click', (e) => e.preventDefault());
    }
  }

  // Determine the closest edge based on cursor position
  getEntryDirection(event) {
    const rect = this.mainDetailsToggle.getBoundingClientRect();
    const cursorX = event.clientX;
    const cursorY = event.clientY;

    // Calculate distances to each edge
    const distances = {
      left: Math.abs(cursorX - rect.left),
      right: Math.abs(cursorX - rect.right),
      top: Math.abs(cursorY - rect.top),
      bottom: Math.abs(cursorY - rect.bottom)
    };

    // Find the edge with the minimum distance
    let minDistance = Infinity;
    let direction = 'left';
    for (const [edge, distance] of Object.entries(distances)) {
      if (distance < minDistance) {
        minDistance = distance;
        direction = edge;
      }
    }

    return direction;
  }

  onMouseEnter(event) {
    // Kill any ongoing animation
    if (this.animation) {
      this.animation.kill();
    }

    // Open the menu
    if (!this.mainDetailsToggle.hasAttribute('open')) {
      this.mainDetailsToggle.setAttribute('open', '');
    }

    this.summary.setAttribute('aria-expanded', true);

    // Calculate the content's scroll height for animation
    const contentHeight = this.content.scrollHeight;

    // Determine entry direction
    const direction = this.getEntryDirection(event);

    // Set initial transform based on entry direction
    const initialTransform = {
      left: { x: -20, y: 0 },
      right: { x: 20, y: 0 },
      top: { x: 0, y: 0 },
      bottom: { x: 0, y: 0 }
    }[direction];

    // Set initial state for animation
    if (this.content) {
      gsap.set(this.content, {
        x: initialTransform.x,
        y: initialTransform.y,
        opacity: 0,
        maxHeight: 0,
        visibility: 'visible'
      });

      // Animate with GSAP (slide from detected direction)
      this.animation = gsap.to(this.content, {
        opacity: 1,
        maxHeight: contentHeight,
        x: 0, // Move to natural x position
        y: 0, // Move to natural y position
        duration: this.openAnimationDuration,
        ease: 'power1.out', // Matches cubic-bezier(0.25, 0.8, 0.25, 1)
        overwrite: true
      });
    }
  }

  onMouseLeave() {
    // Kill any ongoing animation
    if (this.animation) {
      this.animation.kill();
    }

    // Animate with GSAP (slide up)
    if (this.content) {
      this.animation = gsap.to(this.content, {
        opacity: 0,
        maxHeight: 0,
        y: -50, // Shift upward to simulate bottom-to-top collapse
        x: 0, // Ensure no x offset
        visibility: 'hidden',
        duration: this.closeAnimationDuration,
        ease: 'power1.out', // Same ease as open for consistency
        overwrite: true,
        onComplete: () => {
          // Close the menu after animation and reset transforms
          this.mainDetailsToggle.removeAttribute('open');
          this.summary.setAttribute('aria-expanded', false);
          gsap.set(this.content, { x: 0, y: 0 }); // Reset x and y
        }
      });
    }
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
